import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import {
  Building2,
  MapPin,
  Mail,
  Shield,
  Clock,
  Award,
  DollarSign,
  UserCog,
  ChevronRight,
  Calendar,
  Users,
  BookOpen
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

const Settings: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === 'ADMIN';

  const settingsCategories = [
    {
      title: 'Company Settings',
      description: 'Company information, branding, and general settings',
      icon: Building2,
      path: '/settings/company',
      color: 'bg-blue-100 text-blue-600'
    },
    {
      title: 'Organization',
      description: 'Departments, teams, and organizational structure',
      icon: MapPin,
      path: '/settings/organization',
      color: 'bg-green-100 text-green-600'
    },
    ...(isAdmin ? [{
      title: 'User Management',
      description: 'Manage user roles and permissions',
      icon: UserCog,
      path: '/settings/users',
      color: 'bg-red-100 text-red-600'
    }] : []),
    {
      title: 'Leave Management',
      description: 'Leave policies, types, and public holidays',
      icon: Calendar,
      path: '/leaves/policies',
      color: 'bg-purple-100 text-purple-600'
    },
    {
      title: 'Employees',
      description: 'Employee management and profiles',
      icon: Users,
      path: '/employees',
      color: 'bg-orange-100 text-orange-600'
    },
    {
      title: 'Skills Library',
      description: 'Manage organizational skills and competencies',
      icon: BookOpen,
      path: '/skills',
      color: 'bg-yellow-100 text-yellow-600'
    },
    {
      title: 'Payroll',
      description: 'Salary structures, benefits, and payroll configuration',
      icon: DollarSign,
      path: '/payroll/salary-structures',
      color: 'bg-teal-100 text-teal-600'
    },
    {
      title: 'Performance',
      description: 'Goals, reviews, and performance settings',
      icon: Award,
      path: '/goals-and-reviews',
      color: 'bg-pink-100 text-pink-600'
    },
    {
      title: 'Shift Planning',
      description: 'Shift templates and scheduling settings',
      icon: Clock,
      path: '/shift-planning/settings',
      color: 'bg-indigo-100 text-indigo-600'
    },
  ];

  const quickActions = [
    { label: 'Update Company Information', path: '/settings/company', icon: Building2 },
    { label: 'Manage Leave Policies', path: '/leaves/policies', icon: Calendar },
    { label: 'Configure Salary Structures', path: '/payroll/salary-structures', icon: DollarSign },
    { label: 'View Public Holidays', path: '/leaves/holidays', icon: Calendar },
    { label: 'Manage Skills Library', path: '/skills', icon: BookOpen },
    { label: 'Shift Planning Settings', path: '/shift-planning/settings', icon: Clock },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Manage your HR system configuration and preferences</h1>
       
      </div>

      {/* Settings Categories Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {settingsCategories.map((category) => (
          <Card
            key={category.path + category.title}
            className="hover:shadow-lg transition-all duration-200 cursor-pointer group border-gray-200 hover:border-primary/50"
            onClick={() => navigate(category.path)}
          >
            <CardHeader className="pb-2">
              <div className="flex items-start space-x-3">
                <div className={`p-2.5 rounded-lg ${category.color}`}>
                  <category.icon className="h-5 w-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <CardTitle className="text-base font-semibold truncate">{category.title}</CardTitle>
                </div>
                <ChevronRight className="h-4 w-4 text-gray-400 group-hover:text-primary transition-colors" />
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              <CardDescription className="text-sm line-clamp-2">{category.description}</CardDescription>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Quick Actions</CardTitle>
          <CardDescription>Common settings tasks</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {quickActions.map((action) => (
              <Button
                key={action.path + action.label}
                variant="outline"
                className="justify-start h-auto py-3 px-4 bg-white hover:bg-gray-50 hover:border-primary/50 transition-colors text-gray-900 hover:text-gray-900"
                onClick={() => navigate(action.path)}
              >
                <action.icon className="h-4 w-4 mr-3 text-gray-500 group-hover:text-gray-700" />
                <span className="text-sm text-gray-700">{action.label}</span>
              </Button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* System Information */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">System Information</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <p className="text-muted-foreground">Version</p>
              <p className="font-medium">1.0.0</p>
            </div>
            <div>
              <p className="text-muted-foreground">Database</p>
              <p className="font-medium">MongoDB</p>
            </div>
            <div>
              <p className="text-muted-foreground">API Status</p>
              <p className="font-medium text-green-600">● Connected</p>
            </div>
            <div>
              <p className="text-muted-foreground">Last Updated</p>
              <p className="font-medium">{new Date().toLocaleDateString()}</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Settings;
